import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeModeContext = createContext(null);
const THEME_PREFERENCE_KEY = 'themeModePreference';

/** Purpose: read the active theme and theme actions. Why: UI components should use the shared persisted theme instead of independent toggles. */
export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};

/** Purpose: initialize, persist, and apply the workspace light/dark theme. Why: one root-level provider keeps theme state and document styling synchronized. */
export const ThemeModeProvider = ({ children }) => {
  // Start every new session in light mode. The old `themeMode` key was written
  // automatically with a dark default, so it must not be treated as a choice.
  const [mode, setMode] = useState(() => (
    localStorage.getItem(THEME_PREFERENCE_KEY) === 'dark' ? 'dark' : 'light'
  ));

  useEffect(() => {
    // Purpose: apply theme mode to the document root. Why: global CSS/Tailwind variants need one authoritative class and dataset value.
    const root = document.documentElement;
    root.classList.toggle('dark', mode === 'dark');
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
  }, [mode]);

  /** Purpose: validate and persist a requested theme. Why: normalizing here keeps every caller from storing unsupported mode values. */
  const updateMode = useCallback((nextMode) => {
    setMode((currentMode) => {
      const resolvedMode = typeof nextMode === 'function' ? nextMode(currentMode) : nextMode;
      const normalizedMode = resolvedMode === 'dark' ? 'dark' : 'light';
      localStorage.setItem(THEME_PREFERENCE_KEY, normalizedMode);
      return normalizedMode;
    });
  }, []);

  /** Purpose: publish stable theme context actions/state. Why: memoization avoids rerendering every consumer when unrelated parent state changes. */
  const value = useMemo(() => ({
    mode,
    setMode: updateMode,
    /** Purpose: Handle toggle mode in the theme mode context module. Why: Keeping this action in one handler makes its state or request update consistent. */
    toggleMode: () => updateMode((current) => (current === 'dark' ? 'light' : 'dark')),
  }), [mode, updateMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};
